// utils/text/mdLite.ts

/** Escape HTML special chars (defense-in-depth) */
export function escapeHtml(src: string): string {
  return (src ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

/**
 * MD-Lite → HTML for final render
 * Supports: **bold**, *italic* / _italic_, __underline__, \n and paragraphs
 */
export function mdLiteToHtml(raw: string): string {
  // 1) escape
  let s = escapeHtml(raw);

  // 2) normalize newlines
  s = s.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n");

  // 3) underline: __text__
  s = s.replace(/__([^_\n][\s\S]*?)__/g, "<u>$1</u>");

  // 4) italic: *text* (single asterisk, not bold)
  s = s.replace(/(^|[^\*])\*(?!\*)([^*\n]+?)\*(?!\*)/g, "$1<em>$2</em>");

  // 5) italic: _text_ (single underscore, not underline)
  s = s.replace(/(^|[^_])_(?!_)([^_\n]+?)_(?!_)/g, "$1<em>$2</em>");

  // 6) bold: **text**
  s = s.replace(/\*\*([\s\S]+?)\*\*/g, "<strong>$1</strong>");

  // 7) paragraphs: split by double \n; single \n → <br>
  const parts = s.split(/\n{2}/g).map(p => `<p>${p.replace(/\n/g, "<br />")}</p>`);
  return parts.join("");
}

/**
 * MD-Lite → HTML for live preview under a textarea
 * Keeps markers as invisible spans to preserve width and caret sync.
 * No <p> wrapping; converts \n → <br> only (to match textarea pre-wrap).
 */
export function mdLiteToPreviewHtml(raw: string): string {
  // 1) escape + normalize newlines
  let s = escapeHtml(raw || "");
  s = s.replace(/\r\n/g, "\n");

  // 2) underline with hidden markers
  // __text__ → <span class="md-marker">__</span><u>text</u><span class="md-marker">__</span>
  s = s.replace(/__([^_\n][\s\S]*?)__/g, (_m, g1) =>
    `<span class="md-marker">__</span><u>${g1}</u><span class="md-marker">__</span>`
  );

  // 3) italic (*) with hidden markers (avoid **)
  s = s.replace(/(^|[^\*])\*(?!\*)([^*\n]+?)\*(?!\*)/g, (_m, pre, g1) =>
    `${pre}<span class="md-marker">*</span><em>${g1}</em><span class="md-marker">*</span>`
  );

  // 4) italic (_) with hidden markers (avoid __)
  s = s.replace(/(^|[^_])_(?!_)([^_\n]+?)_(?!_)/g, (_m, pre, g1) =>
    `${pre}<span class="md-marker">_</span><em>${g1}</em><span class="md-marker">_</span>`
  );

  // 5) bold with hidden markers
  s = s.replace(/\*\*([\s\S]+?)\*\*/g, (_m, g1) =>
    `<span class="md-marker">**</span><strong>${g1}</strong><span class="md-marker">**</span>`
  );

  // 6) single newline → <br> (no paragraphs)
  s = s.replace(/\n/g, "<br />");

  return s;
}


/** Strip md-lite markers to a single-line plain text (for InlineField) */
export function mdLiteToPlain(raw: string): string {
  if (!raw) return "";
  let s = raw;

  // remove underline
  s = s.replace(/__([^_\n][\s\S]*?)__/g, "$1");
  // remove bold
  s = s.replace(/\*\*([\s\S]+?)\*\*/g, "$1");
  // remove italic (*...* and _..._)
  s = s.replace(/(^|[^\*])\*(?!\*)([^*\n]+?)\*(?!\*)/g, "$1$2");
  s = s.replace(/(^|[^_])_(?!_)([^_\n]+?)_(?!_)/g, "$1$2");

  // collapse newlines to space
  s = s.replace(/\r\n/g, "\n").replace(/\n+/g, " ");

  // normalize whitespaces
  return s.replace(/\s{2,}/g, " ").trim();
}



/* ---------------- HTML <-> MD-Lite ---------------- */
export function htmlToMdLite(html: string): string {
  let s = html;
  // normalize
  s = s.replace(/<br\s*\/?>/gi, "\n");
  s = s.replace(/<\/p>\s*<p>/gi, "\n\n").replace(/^<p>/i, "").replace(/<\/p>$/i, "");
  s = s.replace(/<\/?div[^>]*>/gi, "\n");

  // inline formatting → md-lite
  s = s.replace(/<strong[^>]*>([\s\S]*?)<\/strong>/gi, "**$1**"); // catches data-lit too
  s = s.replace(/<b>([\s\S]*?)<\/b>/gi, "**$1**");
  s = s.replace(/<em>([\s\S]*?)<\/em>/gi, "*$1*");
  s = s.replace(/<i>([\s\S]*?)<\/i>/gi, "*$1*");
  s = s.replace(/<u>([\s\S]*?)<\/u>/gi, "__$1__");

  // strip remaining tags
  s = s.replace(/<[^>]+>/g, "");
  // newlines
  s = s.replace(/\r\n/g, "\n").replace(/\n{3,}/g, "\n\n").trim();
  return s;
}

export function ensureBlocks(html: string): string {
  if (!html || !html.trim()) return "<p></p>";
  if (/<p[\s>]/i.test(html)) return html;
  const parts = html.split(/\n{2}/g).map((p) => `<p>${p.replace(/\n/g, "<br>")}</p>`);
  return parts.join("");
}

/* -------- exec برای غیر Bold -------- */
export function exec(cmd: "italic" | "underline" | "insertLineBreak" | "insertParagraph") {
  document.execCommand(cmd, false);
}